export function formatPlainSummary(summaryText) {
  if (!summaryText) return "No summary available";

  const safe = summaryText.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = safe.split("\n");
  let html = "";
  let inList = false;

  for (let line of lines) {
    line = line.trim();

    if (line.startsWith("•")) {
      if (!inList) {
        html += "<ul style='margin:6px 0 6px 16px;'>";
        inList = true;
      }
      html += `<li>${line.replace(/^•\s*/, "")}</li>`;
    } else {
      if (inList) {
        html += "</ul>";
        inList = false;
      }

      if (line === "") {
        html += "<br/>";
      } else {
        const labelMatch = line.match(/^([^:]+):\s*(.*)$/);
        if (labelMatch) {
          // render label as plain text (no bold) to avoid styling bleeding into list items
          html += `<div style="margin-left:4px; margin-bottom:4px;">
            ${labelMatch[1]}: ${labelMatch[2]}
          </div>`;
        } else {
          html += `<div style="margin-left:4px; margin-bottom:4px;">${line}</div>`;
        }
      }
    }
  }

  if (inList) html += "</ul>";
  return html;
}



//  HTML → PLAIN TEXT WITH STRUCTURE
export function htmlToPlainWithStructure(html) {
  if (!html) return "";

  const temp = document.createElement("div");
  temp.innerHTML = html;

  function walk(node) {
    let output = "";

    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        output += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();

        if (tag === "br") {
          output += "\n";
        } else if (tag === "p" || tag === "div") {
          output += walk(child) + "\n";
        } else if (tag === "li") {
          output += "• " + walk(child) + "\n";
        } else if (tag === "strong" || tag === "b") {
          output += "**" + walk(child) + "**";
        } else if (tag === "em" || tag === "i") {
          output += "*" + walk(child) + "*";
        } else {
          output += walk(child);
        }
      }
    });

    return output;
  }

  return walk(temp)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}



//  SAFE BASE64 DECODER
export function safeBase64Decode(value, label = "") {
  if (!value) return "";
  try {
    return atob(value);
  } catch (e) {
    // console.warn(`Base64 decode failed for ${label}:`, value);
    return value;
  }
}



//  TRANSCRIPT NORMALIZER
export function normalizeTranscript(raw) {
  if (!raw) return "No transcript available";

  const data = raw?.data || raw;
  const transcriptArray =
    data?.Calls?.transcript ||
    data?.transcript ||
    [];

  if (!Array.isArray(transcriptArray) || transcriptArray.length === 0) {
    return "No transcript available";
  }

  return transcriptArray
    .map((item) => {
      const role = item.role || "Unknown";
      const text = item.text || "";
      const timestamp = item.timestamp ? formatTimestamp(item.timestamp) : null;

      return timestamp
        ? `[${timestamp}] ${role}: ${text}`
        : `${role}: ${text}`;
    })
    .join("\n");
}


function formatTimestamp(isoString) {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour12: false });
  } catch {
    return isoString;
  }
}



 //  DATE FORMATTING
 export function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const date = new Date(iso);

    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    return date.toLocaleString("en-US", options).replace(",", "");
  } catch {
    return iso;
  }
}



 //  HTML DECODER
 export function decodeHtml(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}



 
// Strip HTML → plain text
export function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ");
}


// Extract case number
export function extractCaseNumber(text) {
  // Decode HTML entities first so sequences like "&lt;Enter Case Number(s)&gt;" don't confuse the regex.
  const decoded = decodeHtml(String(text || ""));
  const cleaned = stripHtml(decoded).replace(/[*•]/g, " ").replace(/\u00A0/g, " ");

  // 1) Primary: explicit labels - support various label forms: "Case #", "Case No.", "Case Number(s)"
  const labelRe = /\bcase(?:\s*(?:number|no|#|number\(s\)))?\b[\s:-]*([0-9][0-9\s,-]*)/i;
  const m = cleaned.match(labelRe);
  if (m && m[1]) {
    return m[1].replace(/[^\d]/g, "") || null;
  }

  // 2) Broader forms: e.g. "Case Number(s): 123-456" (alternate spacing/characters)
  const altRe = /\bcase(?:\s*(?:number|no|#))\b[^0-9]{0,5}([0-9][0-9\s,-]*)/i;
  const m2 = cleaned.match(altRe);
  if (m2 && m2[1]) {
    return m2[1].replace(/[^\d]/g, "") || null;
  }

  // 3) Narrow proximity fallback: locate any 6+ digit token that appears near the word "case"
  // This avoids picking unrelated numbers like phone numbers elsewhere in the summary.
  const sixPlus = cleaned.match(/([0-9][0-9\-\s,]{5,})/g);
  if (sixPlus && sixPlus.length) {
    const lower = cleaned.toLowerCase();
    for (const token of sixPlus) {
      const plain = token.replace(/[^\d]/g, "");
      // find token position and check for word "case" within ~30 chars before it
      const idx = lower.indexOf(token.toLowerCase());
      if (idx === -1) continue;
      const contextStart = Math.max(0, idx - 30);
      const context = lower.slice(contextStart, idx);
      if (context.includes("case")) {
        return plain;
      }
    }
  }
  // no match
  return null;
}

// Extract ALL case numbers from text — returns an array (may have 0, 1, or many entries).
// Uses the same matching logic as extractCaseNumber but splits the raw match on commas/spaces.
export function extractCaseNumbers(text) {
  const decoded = decodeHtml(String(text || ""));
  const cleaned = stripHtml(decoded).replace(/[*•]/g, " ").replace(/\u00A0/g, " ");

  let rawMatch = null;

  const labelRe = /\bcase(?:\s*(?:number|no|#|number\(s\)))?\b[\s:-]*([0-9][0-9\s,-]*)/i;
  const m = cleaned.match(labelRe);
  if (m && m[1]) rawMatch = m[1];

  if (!rawMatch) {
    const altRe = /\bcase(?:\s*(?:number|no|#))\b[^0-9]{0,5}([0-9][0-9\s,-]*)/i;
    const m2 = cleaned.match(altRe);
    if (m2 && m2[1]) rawMatch = m2[1];
  }

  if (!rawMatch) {
    const sixPlus = cleaned.match(/([0-9][0-9\-\s,]{5,})/g);
    if (sixPlus && sixPlus.length) {
      const lower = cleaned.toLowerCase();
      for (const token of sixPlus) {
        const idx = lower.indexOf(token.toLowerCase());
        if (idx === -1) continue;
        const contextStart = Math.max(0, idx - 30);
        const context = lower.slice(contextStart, idx);
        if (context.includes("case")) {
          rawMatch = token;
          break;
        }
      }
    }
  }

  if (!rawMatch) return [];

  return rawMatch.split(/[,\s]+/).map((s) => s.replace(/[^\d]/g, "")).filter(Boolean);
}


// Validate numeric case number
export function validateCaseNumberFormat(num) {
  if (!num?.trim()) return "Enter a case number";
  if (!/^\d+$/.test(num.trim())) return "Digits only";
  return null;
}


// Close popup safely
export function closePopupWindow() {
  try {
    if (window.opener) return window.close();
    window.location.href = "about:blank";
  } catch {
    window.location.href = "about:blank";
  }
}

// Decode HTML entities once (safe normalization of server-returned HTML).
// If server returns fully-escaped HTML like "&lt;p&gt;&lt;test&gt;&lt;/p&gt;", this
// will decode it to "<p>&lt;test&gt;</p>" which then renders as "<test>" inside the <p>.
// If HTML is already normal (<p>&lt;test&gt;</p>), this will leave it unchanged.
//
// Additionally: sanitize unknown element tags (e.g. <test>) by replacing them with
// a visible escaped representation so they render literally instead of being treated as DOM nodes.
export function normalizeServerHtml(html) {
  if (html == null) return html;
  const s = String(html);

  // 1) Decode one layer of HTML entities if present
  let decoded = s;
  if (s.includes("&lt;") || s.includes("&gt;") || s.includes("&amp;")) {
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = s;
      decoded = txt.value;
    } catch (e) {
      // console.warn("[utils] normalizeServerHtml decode failed:", e);
      decoded = s;
    }
  }

  // Safety: escape any '<...>' sequences whose tag name is NOT in our allowed set.
  // This escapes both the '<' and the trailing '>' so the browser will not parse
  // the fragment as an element. We handle opening tags and closing tags.
  try {
    const allowed = new Set([
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "mark",
      "ul",
      "ol",
      "li",
      "div",
      "span",
      "a",
      "blockquote",
    ]);

    // Escape opening tags for non-allowed tag names: <tag ...>
    decoded = decoded.replace(/<\s*([a-zA-Z][\w:-]*)(\b[^>]*)>/g, (m, tagName, rest) => {
      if (allowed.has(tagName.toLowerCase())) {
        return m; // keep allowed tags intact
      }
      // escape entire match so it's safe text
      const escaped = `&lt;${tagName}${(rest || "").replace(/>/g, "&gt;")}&gt;`;
      return escaped;
    });

    // Escape closing tags for non-allowed tag names: </tag>
    decoded = decoded.replace(/<\s*\/\s*([a-zA-Z][\w:-]*)\s*>/g, (m, tagName) => {
      if (allowed.has(tagName.toLowerCase())) {
        return m;
      }
      return `&lt;/${tagName}&gt;`;
    });
  } catch (e) {
    // console.warn("[utils] normalizeServerHtml escape malformed '<' failed:", e);
  }

  // 2) Parse and sanitize: replace any element whose tag is not allowed with a visible escaped string
  try {
    const container = document.createElement("div");
    container.innerHTML = decoded;

    const allowed = new Set([
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "mark",
      "ul",
      "ol",
      "li",
      "div",
      "span",
      "a",
      "blockquote",
    ]);

    // Serialize children into a visible string:
    function serializeChildrenToVisible(node) {
      let out = "";
      Array.from(node.childNodes).forEach((c) => {
        if (c.nodeType === Node.TEXT_NODE) {
          out += c.textContent;
        } else if (c.nodeType === Node.ELEMENT_NODE) {
          const tag = c.tagName.toLowerCase();
          if (!allowed.has(tag)) {
            // represent unknown element as escaped opening tag + its serialized children
            // NOTE: omit emitting a closing tag here (per requirement)
            out += `&lt;${tag}&gt;${serializeChildrenToVisible(c)}`;
          } else {
            // keep allowed elements as-is (preserve structure)
            const temp = document.createElement("div");
            temp.appendChild(c.cloneNode(true));
            Array.from(temp.childNodes).forEach((child) => {
              if (child.nodeType === Node.ELEMENT_NODE) {
                child.innerHTML = normalizeServerHtml(child.innerHTML);
              }
            });
            out += temp.innerHTML;
          }
        }
      });
      return out;
    }

    // Walk and replace disallowed elements with visible serialized text
    function walkAndReplace(node) {
      const children = Array.from(node.childNodes);
      children.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName.toLowerCase();
          if (!allowed.has(tag)) {
            // produce opening escaped tag + serialized children (no closing tag)
            const visible = `&lt;${tag}&gt;${serializeChildrenToVisible(child)}`;
            const wrapper = document.createElement("span");
            wrapper.innerHTML = visible;
            node.replaceChild(wrapper, child);
          } else {
            walkAndReplace(child);
          }
        }
      });
    }

    walkAndReplace(container);

    // Finally, ensure any remaining escaped closing tags for disallowed tags are removed
    // and remove empty paragraphs / collapse repeated <br/> to reduce extra blank space.
    let cleaned = stripUnknownClosingTags(container.innerHTML);

    // remove entirely-empty paragraphs like: <p></p> or <p><br></p> or <p>&nbsp;</p>
    cleaned = cleaned.replace(/<p>(\s|&nbsp;|(?:<br\s*\/?>))*<\/p>/gi, "");

    // collapse consecutive <br/> into a single <br/>
    cleaned = cleaned.replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>");

    // collapse repeated blank lines (in case there are plain \n sequences)
    cleaned = cleaned.replace(/\n{3,}/g, "\n\n");

    return cleaned;
  } catch (e) {
    // console.warn("[utils] normalizeServerHtml sanitize failed:", e);
    // fallback sanitize and collapse
    let fallback = stripUnknownClosingTags(decoded);
    fallback = fallback.replace(/<p>(\s|&nbsp;|(?:<br\s*\/?>))*<\/p>/gi, "");
    fallback = fallback.replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>");
    return fallback;
  }
}

// Provide a function to remove closing tags (raw and escaped) for disallowed/custom tags.
// Keeps closing tags for allowed tags only.
export function stripUnknownClosingTags(html) {
  if (html == null) return html;
  const s = String(html);
  const allowed = new Set([
    "p","br","strong","b","em","i","u","s","mark","ul","ol","li","div","span","a","blockquote",
  ]);

  // remove raw closing tags for disallowed tags
  const withoutRaw = s.replace(/<\/([a-zA-Z0-9:-]+)\s*>/g, (m, tag) => {
    return allowed.has(tag.toLowerCase()) ? m : "";
  });

  // remove escaped closing tags (&lt;/tag&gt;) for disallowed tags
  const withoutEscaped = withoutRaw.replace(/&lt;\/([a-zA-Z0-9:-]+)&gt;/g, (m, tag) => {
    return allowed.has(tag.toLowerCase()) ? m : "";
  });

  return withoutEscaped;
}

// Remove leading empty blocks (e.g. <p><br></p>, <p>&nbsp;</p>, stray <br>) so editor
// doesn't show an extra blank line before lists when opening edit mode.
export function trimLeadingEmptyBlocks(html) {
  if (!html) return html || "";
  try {
    const container = document.createElement("div");
    container.innerHTML = String(html);

    // remove leading nodes while they are "empty" blocks
    while (container.firstChild) {
      const n = container.firstChild;
      // text node with whitespace -> remove
      if (n.nodeType === Node.TEXT_NODE && !n.textContent.trim()) {
        container.removeChild(n);
        continue;
      }

      // element nodes: consider empty if they contain only <br>, &nbsp; or whitespace
      if (n.nodeType === Node.ELEMENT_NODE) {
        // Do NOT strip &nbsp; here — only treat nodes empty when they contain no visible text
        const inner = n.innerHTML.replace(/\s|<br\s*\/?>/g, "");
        if (inner === "") {
          container.removeChild(n);
          continue;
        }
      }

      // otherwise first meaningful node reached
      break;
    }

    return container.innerHTML;
  } catch (e) {
    // console.warn("[utils] trimLeadingEmptyBlocks failed:", e);
    return html;
  }
}

// Remove empty blocks (e.g. <p><br></p>, <p>&nbsp;</p>, stray <br/>) that appear immediately before lists.
// This prevents a visible blank line appearing above <ul> or <ol>.
export function removeEmptyBlocksBeforeLists(html) {
  if (!html) return html || "";
  try {
    // Remove sequences like: <p>...empty...</p>\s*<ul or <ol
    let s = String(html);

    // Normalize self-closing br
    s = s.replace(/<br\s*\/?>/gi, "<br/>");

    // Remove an empty paragraph or multiple empty paragraphs before a list tag
    s = s.replace(/(?:<p>(?:\s|&nbsp;|(?:<br\/>))*<\/p>\s*)+(?=<\s*(?:ul|ol)\b)/gi, "");

    // Also remove stray <br/> lines immediately before lists
    s = s.replace(/(?:<br\/>\s*)+(?=<\s*(?:ul|ol)\b)/gi, "");

    return s;
  } catch (e) {
    // console.warn("[utils] removeEmptyBlocksBeforeLists failed:", e);
    return html;
  }
}

// Normalize whitespace in HTML:
// - remove empty paragraphs (<p><br></p>, <p>&nbsp;</p>, etc.)
// - collapse consecutive <br/> into one
// - remove whitespace between tags where it can produce blank lines
// - trim leading/trailing whitespace
export function normalizeWhitespaceInHtml(html) {
  if (html == null) return html || "";
  try {
    let s = String(html);

    // Remove entirely-empty paragraphs anywhere
    s = s.replace(/<p>(\s|&nbsp;|(?:<br\s*\/?>))*<\/p>/gi, "");

    // Collapse consecutive <br/> (or <br>) into a single <br/>
    s = s.replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>");

    // Remove whitespace between tags which can render as blank lines
    s = s.replace(/>\s+</g, "><");

    // Trim leading/trailing whitespace
    return s.trim();
  } catch (e) {
    // console.warn("[utils] normalizeWhitespaceInHtml failed:", e);
    return html;
  }
}

// Remove all visible line-breaks from HTML while preserving words.
// - remove <br> tags
// - replace paragraph tags with a single space
// - remove newline characters and collapse repeated whitespace
export function stripLineBreaksHtml(html) {
  if (html == null) return "";
  try {
    let s = String(html);

    // Remove any <br> tags (both <br> and <br/>)
    s = s.replace(/<br\s*\/?>/gi, " ");

    // Replace paragraph boundaries with a space, remove opening/closing p tags
    s = s.replace(/<\/p>\s*<p[^>]*>/gi, " "); // between paragraphs -> single space
    s = s.replace(/<\/?p[^>]*>/gi, " ");

    // Remove any remaining newline characters
    s = s.replace(/[\r\n]+/g, " ");

    // Collapse multiple whitespace into single space
    s = s.replace(/\s{2,}/g, " ").trim();

    return s;
  } catch (e) {
    // console.warn("[utils] stripLineBreaksHtml failed:", e);
    return String(html || "").replace(/[\r\n]+/g, " ");
  }
}

// TipTap wraps every list item's text in a <p>: <li><p>text</p></li>.
// CKEditor (and most other editors) expect bare content: <li>text</li>.
// The <p> inside <li> causes extra spacing and alignment breaks in CKEditor.
// This strips those <p> wrappers while keeping nested <ul>/<ol> and inline marks intact.
export function unwrapListItemParagraphs(html) {
  if (!html) return html || "";
  try {
    const div = document.createElement("div");
    div.innerHTML = String(html);
    div.querySelectorAll("li").forEach((li) => {
      Array.from(li.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE && child.tagName === "P") {
          // Move all children of the <p> directly into the <li> before the <p>, then remove <p>
          while (child.firstChild) li.insertBefore(child.firstChild, child);
          li.removeChild(child);
        }
      });
    });
    return div.innerHTML;
  } catch (_) {
    return html;
  }
}

// Clean HTML for final submit: run the same sequence used elsewhere but combined,
// and strip empty wrapper tags (div/span) that can accumulate across edits.
export function cleanHtmlForSubmit(html) {
  if (html == null) return "";
  try {
    let s = String(html || "");

    const hasLists = /<[ou]l>|<li>/i.test(s);

    s = trimLeadingEmptyBlocks(s);
    s = removeEmptyBlocksBeforeLists(s);
    s = normalizeWhitespaceInHtml(s);

    s = s.replace(
      /<(?:(?:div|span)[^>]*)>\s*(?:<br\s*\/?>|\s)*\s*<\/(?:div|span)>/gi,
      ""
    );

    s = stripUnknownClosingTags(s);

    if (!hasLists) {
      s = removeEditorLineBreaks(s);
    }

    // Strip TipTap's <p> wrappers inside <li> for cross-editor compatibility
    s = unwrapListItemParagraphs(s);

    s = trimTextNodesInHtml(s);

    return s.trim();
  } catch (e) {
    return String(html || "").trim();
  }
}

// Remove line breaks/blocks that are generated by the editor (empty paragraphs, multiple <br/>)
// but preserve actual paragraphs and single <br/> that likely came from backend.
export function removeEditorLineBreaks(html) {
  if (html == null) return "";
  try {
    let s = String(html);

    // Remove entirely-empty paragraphs like <p></p>, <p><br/></p>, <p>&nbsp;</p>
    s = s.replace(/<p>(?:\s|&nbsp;|(?:<br\s*\/?>))*<\/p>/gi, "");

    // Collapse consecutive <br/> into a single <br/>
    s = s.replace(/(?:<br\s*\/?>\s*){2,}/gi, "<br/>");

    // Trim excessive whitespace between tags (but keep tag boundaries)
    s = s.replace(/>\s+</g, "><");

    return s.trim();
  } catch (e) {
    // console.warn("[utils] removeEditorLineBreaks failed:", e);
    return String(html || "");
  }
}

// New: trim and collapse whitespace inside text nodes while preserving structure.
// - collapse runs of whitespace into single space inside each text node
// - remove text nodes that become empty after trimming
// - preserves tag structure (operates on DOM)
export function trimTextNodesInHtml(html) {
  if (html == null) return "";
  try {
    const container = document.createElement("div");
    container.innerHTML = String(html);

    function walk(node) {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        if (child.nodeType === Node.TEXT_NODE) {
          // Collapse runs of ordinary whitespace to single spaces but preserve NBSP (\u00A0).
          // Trim only ordinary whitespace at edges (don't remove leading/trailing NBSP).
          const txt = String(child.textContent || "");
          // Replace sequences of normal whitespace (space, tab, newline, etc.) with a single space.
          const collapsedMiddle = txt.replace(/[ \t\n\r\f\v]+/g, " ");
          // Trim ordinary whitespace at start/end but keep NBSP (\u00A0) intact.
          const trimmed = collapsedMiddle.replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, "");
          if (trimmed === "") {
            node.removeChild(child);
          } else {
            child.textContent = trimmed;
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      }
    }

    walk(container);
    return container.innerHTML;
  } catch (e) {
    // console.warn("[utils] trimTextNodesInHtml failed:", e);
    // fallback: collapse whitespace globally
    try {
      return String(html).replace(/[ \t\n\r\f\v]+/g, " ").replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, "");
    } catch {
      return String(html || "");
    }
  }
}
